import { Link, Navigate } from 'react-router-dom';
import { Calendar, Banknote, Clock } from 'lucide-react';
import { useAppStore } from '../store';
import { useViewMode } from '../contexts/ViewModeContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

const HomePage = () => {
  const { homes, bookings, bragPosts, currentUser } = useAppStore();
  const { isGuestView } = useViewMode();

  // Redirect to properties page in guest view or for family members
  if (isGuestView || currentUser?.isFamilyMember) {
    return <Navigate to="/homes" replace />;
  }

  const upcomingBookings = bookings.filter(
    booking => booking.userId === currentUser?.id && booking.startDate > new Date()
  ).slice(0, 3);

  const recentBrags = bragPosts.slice(-3).reverse();

  const stats = [
    { label: 'Unterkünfte', value: homes.length },
    { label: 'Buchungen', value: bookings.length },
    { label: 'Brags', value: bragPosts.length },
    { label: 'Aktive Nutzer', value: 3 },
  ];

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'approved': return 'default';
      case 'pending': return 'secondary';
      case 'cancelled': return 'destructive';
      default: return 'outline';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'approved': return 'Genehmigt';
      case 'pending': return 'Ausstehend';
      case 'cancelled': return 'Storniert';
      default: return status;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Hero Section */}
      <section className="text-center py-16 mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6 tracking-tight">
          Willkommen bei Holiday Homes der Familie Richner
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
          Entdecke unsere Ferienhäuser, buche deinen nächsten Aufenthalt und zeig, was du zu den Unterkünften beigetragen hast.
        </p>
        <div className="flex gap-4 justify-center flex-col sm:flex-row">
          <Button asChild>
            <Link to="/homes">Unterkünfte entdecken</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/bookings">Meine Buchungen</Link>
          </Button>
        </div>
      </section>

      {/* Stats Section */}
      <section className="mb-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat, index) => (
            <Card key={index} className="hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-primary mb-1">{stat.value}</div>
                <div className="text-xs uppercase font-semibold tracking-wider text-muted-foreground">
                  {stat.label}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Separator className="my-8" />

      {/* Content Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        {/* Upcoming Bookings */}
        <section>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">Bevorstehende Buchungen</h2>
            <Button variant="ghost" asChild>
              <Link to="/bookings">Alle anzeigen</Link>
            </Button>
          </div>

          {upcomingBookings.length > 0 ? (
            <div className="space-y-4">
              {upcomingBookings.map((booking) => {
                const home = homes.find(h => h.id === booking.homeId);
                const daysUntil = Math.ceil((booking.startDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

                return (
                  <Card key={booking.id} className="hover:shadow-md transition-all duration-200 hover:-translate-y-0.5">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start gap-4 mb-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-primary mb-2">
                            {home?.name || 'Unbekannte Unterkunft'}
                          </h3>
                          <div className="space-y-1.5 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              <span>{booking.startDate.toLocaleDateString('de-DE')} – {booking.endDate.toLocaleDateString('de-DE')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Banknote className="h-4 w-4" />
                              <span>CHF {booking.totalPrice}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              <span>noch {daysUntil} {daysUntil === 1 ? 'Tag' : 'Tage'}</span>
                            </div>
                          </div>
                        </div>
                        <Badge variant={getStatusVariant(booking.status)}>
                          {getStatusLabel(booking.status)}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <h3 className="text-lg font-semibold mb-2">Keine bevorstehenden Buchungen</h3>
                <p className="text-muted-foreground mb-6">Bereit für dein nächstes Abenteuer?</p>
                <Button asChild>
                  <Link to="/homes">Jetzt buchen</Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </section>

        {/* Recent Brags */}
        <section>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">Neueste Brags</h2>
            <Button variant="ghost" asChild>
              <Link to="/brag-board">Alle anzeigen</Link>
            </Button>
          </div>

          {recentBrags.length > 0 ? (
            <div className="space-y-4">
              {recentBrags.map((brag) => {
                const home = homes.find(h => h.id === brag.homeId);
                const homeName = brag.homeId === 'grounds' ? 'Umgebung / Grundstück' : (home?.name || 'Unbekannte Unterkunft');
                return (
                  <Link key={brag.id} to="/brag-board" className="block">
                    <Card className="overflow-hidden hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-pointer">
                      <img
                        src={brag.imageUrl}
                        alt="Brag"
                        className="w-full h-48 object-cover"
                      />
                      <CardContent className="p-4">
                        <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                          {brag.caption}
                        </p>
                        <div className="flex justify-between items-center pt-3 border-t">
                          <span className="text-sm font-medium text-muted-foreground">
                            {homeName}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {brag.timestamp.toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <h3 className="text-lg font-semibold mb-2">Noch keine Brags geteilt</h3>
                <p className="text-muted-foreground mb-6">Zeig, was du zur Unterkunft beigetragen hast!</p>
                <Button asChild>
                  <Link to="/brag-board">Beitrag erstellen</Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </section>
      </div>
    </div>
  );
};

export default HomePage;
